import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { Field, ChoiceGroup } from './Field';
import { ArrowRight, Check } from './Icons';
import { LOCATIONS, IZI } from '../data/site';
import { sendBookingRequest, sendBookingEmailCopy } from '../services/iziApi';
import '../pages/Form.css';

const GUEST_OPTIONS = [
  ...Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1} ${i === 0 ? 'guest' : 'guests'}`,
  })),
  { value: '13+', label: '13 or more (large group)' },
];

const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 9; h <= 22; h += 1) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
    if (h !== 22) slots.push(`${String(h).padStart(2, '0')}:30`);
  }
  return slots.map((t) => ({ value: t, label: t }));
})();

function validate(values) {
  const errors = {};
  if (values.name.trim().length < 2) errors.name = 'Please tell us your name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim()))
    errors.email = 'We need a valid email to confirm the table.';
  if (values.phone.replace(/\D/g, '').length < 9)
    errors.phone = 'A phone number we can reach you on, please.';
  if (!values.date) errors.date = 'Pick a date.';
  else if (values.date < new Date().toISOString().slice(0, 10))
    errors.date = 'That date has already passed.';
  if (!values.time) errors.time = 'Pick a time.';
  return errors;
}

/**
 * The reservation form. Used standalone on /bookings and inline on every
 * event page. Submits to the Izi API's public contact inbox — see
 * services/iziApi.js for why reservations go through that endpoint.
 */
export default function BookingForm({ defaultBranch, defaultDetails = '' }) {
  const EMPTY = useMemo(
    () => ({
      name: '',
      reservationName: '',
      email: '',
      phone: '',
      branch: defaultBranch || LOCATIONS[0].name,
      date: '',
      time: '19:00',
      guests: '2',
      details: defaultDetails,
    }),
    [defaultBranch, defaultDetails]
  );

  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [serverError, setServerError] = useState('');

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const branch = LOCATIONS.find((l) => l.name === values.branch) || LOCATIONS[0];

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  async function onSubmit(e) {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) {
      document.querySelector('.field.has-error .field__control')?.focus();
      return;
    }
    setStatus('sending');
    setServerError('');
    // Two independent channels: the Izi dashboard inbox and the Netlify Forms
    // email copy. The request counts as received if EITHER lands — only a
    // double failure surfaces an error to the guest.
    const [izi, mail] = await Promise.allSettled([
      sendBookingRequest(values),
      sendBookingEmailCopy(values),
    ]);
    if (izi.status === 'fulfilled' || mail.status === 'fulfilled') {
      setStatus('done');
    } else {
      setStatus('error');
      setServerError(izi.reason?.message || 'Something went wrong. Please try again.');
    }
  }

  if (status === 'done') {
    return (
      <Reveal className="formdone">
        <span className="formdone__icon">
          <Check size={26} />
        </span>
        <h3 className="display brand d3">Request received.</h3>
        <p className="body">
          Thank you, {values.name.split(' ')[0]}. We have your request for{' '}
          <strong>{values.guests}</strong> at <strong>{values.branch}</strong> on{' '}
          <strong>{values.date}</strong> at <strong>{values.time}</strong>. A member of
          the team will confirm by phone or email shortly.
        </p>
        <p className="body">
          If it&apos;s for today or within the next couple of hours, please also call{' '}
          <a href={`tel:${branch.phoneIntl}`} className="tabular formdone__phone">
            {branch.phone}
          </a>{' '}
          so we can hold it for you.
        </p>
        <div className="formdone__actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setValues(EMPTY);
              setStatus('idle');
            }}
          >
            <span>Make another booking</span>
          </button>
          <Link className="btn btn--gold" to="/menus">
            <span>See the menus</span>
            <ArrowRight />
          </Link>
        </div>
      </Reveal>
    );
  }

  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      <Reveal>
        <ChoiceGroup
          label="Which branch?"
          name="branch"
          value={values.branch}
          onChange={set('branch')}
          options={LOCATIONS.map((l) => ({ value: l.name, label: l.name, hint: l.strapline }))}
        />
      </Reveal>

      <Reveal className="form__row" delay={0.05}>
        <Field
          label="Date"
          name="date"
          type="date"
          min={today}
          required
          value={values.date}
          onChange={set('date')}
          error={errors.date}
        />
        <Field
          label="Time"
          name="time"
          as="select"
          required
          value={values.time}
          onChange={set('time')}
          error={errors.time}
          options={TIME_SLOTS}
        />
        <Field
          label="Guests"
          name="guests"
          as="select"
          required
          value={values.guests}
          onChange={set('guests')}
          options={GUEST_OPTIONS}
        />
      </Reveal>

      <Reveal className="form__row form__row--2" delay={0.1}>
        <Field
          label="Your name"
          name="name"
          autoComplete="name"
          required
          placeholder="Who's booking?"
          value={values.name}
          onChange={set('name')}
          error={errors.name}
        />
        <Field
          label="Reservation name"
          name="reservationName"
          placeholder="If the table is under another name"
          value={values.reservationName}
          onChange={set('reservationName')}
        />
      </Reveal>

      <Reveal className="form__row form__row--2" delay={0.15}>
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="you@example.com"
          value={values.email}
          onChange={set('email')}
          error={errors.email}
        />
        <Field
          label="Telephone"
          name="phone"
          type="tel"
          autoComplete="tel"
          required
          placeholder="07XX XXX XXX"
          value={values.phone}
          onChange={set('phone')}
          error={errors.phone}
        />
      </Reveal>

      <Reveal delay={0.2}>
        <Field
          label="Additional details"
          name="details"
          as="textarea"
          placeholder="Birthdays, dietary requirements, a table by the window — tell us anything that helps."
          value={values.details}
          onChange={set('details')}
        />
      </Reveal>

      {serverError ? (
        <p className="form__server" role="alert">
          {serverError} You can also call{' '}
          <a href={`tel:${branch.phoneIntl}`} className="tabular">
            {branch.phone}
          </a>
          .
        </p>
      ) : null}

      <Reveal className="form__foot" delay={0.25}>
        <button type="submit" className="btn btn--gold" disabled={status === 'sending'}>
          <span>{status === 'sending' ? 'Sending…' : 'Request this table'}</span>
          {status === 'sending' ? null : <ArrowRight />}
        </button>
        <p className="form__small">
          A request, not a confirmation — we&apos;ll come back to you to lock it in.
          Staying in instead?{' '}
          <a href={IZI.web} target="_blank" rel="noopener noreferrer" className="form__inline">
            Order on {IZI.brand}
          </a>
          .
        </p>
      </Reveal>
    </form>
  );
}
